import type { TranslationFunction } from "@src/packages/i18n";
import { toLocalDateString } from "@src/packages/utils/date";

function parseIso(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) {
    const fallback = new Date(iso);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  return new Date(year, month, day);
}

function dayDelta(iso: string, now: Date): number | null {
  const target = parseIso(iso);
  if (!target) return null;
  const todayIso = toLocalDateString(now);
  const todayDate = parseIso(todayIso)!;
  return Math.round((target.getTime() - todayDate.getTime()) / 86_400_000);
}

/**
 * Return a locale-aware relative date label:
 *   - today / tomorrow / yesterday
 *   - "Thursday" inside the next 6 days
 *   - "22 juin" / "Jun 22" beyond that
 */
export function formatRelativePhaseDate(
  iso: string,
  locale: string | undefined,
  t: TranslationFunction,
  options: { now?: Date } = {},
): string {
  const now = options.now ?? new Date();
  const target = parseIso(iso);
  if (!target) return iso;

  const delta = dayDelta(iso, now);
  if (delta === 0) return t("cycle_header.today");
  if (delta === 1) return t("cycle_header.tomorrow");
  if (delta === -1) return t("cycle_header.yesterday");
  if (delta !== null && delta > 1 && delta < 7) {
    return target.toLocaleDateString(locale ?? undefined, {
      weekday: "long",
    });
  }
  return target.toLocaleDateString(locale ?? undefined, {
    month: "long",
    day: "numeric",
  });
}

/**
 * Patient-friendly countdown copy for scheduled procedures:
 *   - today (delta 0)
 *   - tomorrow (delta 1)
 *   - "in N days" (delta 2..13)
 *   - "in X weeks" (delta 14+, via existing absolute fallback)
 * Falls back to the absolute date for far-future or unparseable input.
 */
export function formatCountdown(
  iso: string,
  locale: string | undefined,
  t: TranslationFunction,
  options: { now?: Date } = {},
): string {
  const now = options.now ?? new Date();
  const target = parseIso(iso);
  if (!target) return iso;
  const delta = dayDelta(iso, now);
  if (delta === null) return iso;
  if (delta === 0) return t("cycle_header.today");
  if (delta === 1) return t("cycle_header.tomorrow");
  if (delta > 1 && delta < 14) {
    return t("cycle_header.in_n_days", { params: { n: String(delta) } });
  }
  return target.toLocaleDateString(locale ?? undefined, {
    month: "long",
    day: "numeric",
  });
}

/**
 * Long-form date for the sub-line under the countdown:
 *   FR → "vendredi 8 juillet"
 *   EN → "Friday, July 8"
 * Returns the raw ISO if parsing fails.
 */
export function formatPhaseDateLong(
  iso: string,
  locale: string | undefined,
): string {
  const target = parseIso(iso);
  if (!target) return iso;
  return target.toLocaleDateString(locale ?? undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/**
 * Format a "HH:MM" wall-clock time for display in the given locale.
 * Falls back to the raw input if parsing fails.
 */
export function formatPhaseTime(
  hhmm: string,
  locale: string | undefined,
): string {
  const match = /^([0-2]?\d):([0-5]\d)/.exec(hhmm.trim());
  if (!match) return hhmm;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date.toLocaleTimeString(locale ?? undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}
